/**
 * Custom domains route the post-join flow through an extra redirect, which
 * can reset the in-memory modal store before the charity modal ever renders.
 * We persist a "pending" flag per game so the modal keeps re-opening across
 * that redirect/refresh until the user actually interacts with it.
 */
const PENDING_CHARITY_MODAL_KEY = 'pending-charity-modal-games'

type PendingCharityModalMap = Record<string, number>

const readPendingMap = (): PendingCharityModalMap => {
  if (typeof window === 'undefined') return {}

  try {
    const raw = localStorage.getItem(PENDING_CHARITY_MODAL_KEY)
    if (!raw) return {}
    return JSON.parse(raw) as PendingCharityModalMap
  } catch {
    return {}
  }
}

const writePendingMap = (map: PendingCharityModalMap): void => {
  localStorage.setItem(PENDING_CHARITY_MODAL_KEY, JSON.stringify(map))
}

export const hasPendingCharityModal = (gameId: number): boolean => {
  if (typeof window === 'undefined' || Number.isNaN(gameId)) return false
  return Boolean(readPendingMap()[String(gameId)])
}

export const markCharityModalPending = (gameId: number): void => {
  if (typeof window === 'undefined' || Number.isNaN(gameId)) return

  const map = readPendingMap()
  map[String(gameId)] = (map[String(gameId)] ?? 0) + 1
  writePendingMap(map)
}

export const clearPendingCharityModal = (gameId: number): void => {
  if (typeof window === 'undefined' || Number.isNaN(gameId)) return

  const map = readPendingMap()
  if (!(String(gameId) in map)) return

  delete map[String(gameId)]
  writePendingMap(map)
}
